import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TrendingUp, Calendar, DollarSign, ExternalLink } from 'lucide-react';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useDashboardSummary } from '@/hooks/useDashboardSummary';
import { format, startOfWeek, addDays } from 'date-fns';

interface TimesheetStatsProps {
  onNavigate?: (path: string) => void;
}

export function TimesheetStats({ onNavigate }: TimesheetStatsProps) {
  const { timesheets, currentEmployee, projects } = useTimesheet();
  const { getScreenMode } = useDataSource();
  const { summary } = useDashboardSummary(currentEmployee.id, 'dashboard');
  
  const mode = getScreenMode('dashboard');

  const stats = useMemo(() => {
    // Use live data from backend if available and in live mode
    if (mode === 'live' && summary?.performanceMetrics?.billableRevenue && summary?.performanceMetrics?.thisMonth) {
      return {
        currentWeekHours: summary.performanceMetrics.billableRevenue.totalHours,
        billableHours: summary.performanceMetrics.billableRevenue.billableHours,
        billablePercentage: summary.performanceMetrics.billableRevenue.percentage,
        submittedThisMonth: summary.performanceMetrics.thisMonth.submittedCount,
        avgWeeklyHours: summary.performanceMetrics.avgWeeklyHoursLast4Weeks
      };
    }

    // Fall back to demo data calculation
    const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
    const currentWeekEnd = addDays(currentWeek, 6);

    // Current week timesheet
    const currentTimesheet = timesheets.find(
      ts => ts.employeeId === currentEmployee.id &&
      format(ts.weekEnding, 'yyyy-MM-dd') === format(currentWeekEnd, 'yyyy-MM-dd')
    );

    // Calculate current week hours
    const currentWeekHours = currentTimesheet?.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0) || 0;

    // Calculate billable percentage
    const billableHours = currentTimesheet?.entries.reduce((sum, entry) => {
      const project = projects.find(p => p.id === entry.projectId);
      if (project?.billable) {
        return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
      }
      return sum;
    }, 0) || 0;

    const billablePercentage = currentWeekHours > 0 
      ? Math.round((billableHours / currentWeekHours) * 100) 
      : 0;

    // Count submitted timesheets this month
    const thisMonth = new Date().getMonth();
    const submittedThisMonth = timesheets.filter(
      ts => ts.employeeId === currentEmployee.id &&
      ts.status !== 'draft' &&
      ts.weekEnding.getMonth() === thisMonth
    ).length;

    // Calculate average weekly hours (last 4 weeks)
    const last4Weeks = timesheets
      .filter(ts => ts.employeeId === currentEmployee.id && ts.status !== 'draft')
      .slice(0, 4);

    const avgWeeklyHours = last4Weeks.length > 0
      ? last4Weeks.reduce((sum, ts) => {
          return sum + ts.entries.reduce((entrySum, entry) => {
            return entrySum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
          }, 0);
        }, 0) / last4Weeks.length
      : 0;

    return {
      currentWeekHours,
      billableHours,
      billablePercentage,
      submittedThisMonth,
      avgWeeklyHours
    };
  }, [mode, summary, timesheets, currentEmployee, projects]);

  // Determine performance levels for visual accents
  const billableLevel = stats.billablePercentage >= 80 ? 'excellent' : 
                        stats.billablePercentage >= 75 ? 'good' : 
                        stats.billablePercentage >= 60 ? 'warning' : 'critical';
  
  const avgWeeklyLevel = stats.avgWeeklyHours >= 38 ? 'excellent' :
                         stats.avgWeeklyHours >= 35 ? 'good' :
                         stats.avgWeeklyHours >= 30 ? 'warning' : 'critical';

  return (
    <>
        {/* Billable Card - Clickable with visual accents and tooltip */}
        <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => onNavigate?.('/reports/billable')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigate?.('/reports/billable');
              }
            }}
            className={`w-full text-left group/card hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer ${
              billableLevel === 'excellent' ? 'border-l-4 border-l-accent-success-500 bg-gradient-to-br from-accent-success-50/50 to-transparent dark:from-accent-success-900/20' :
              billableLevel === 'good' ? 'border-l-4 border-l-accent-info-500 bg-gradient-to-br from-accent-info-50/50 to-transparent dark:from-accent-info-900/20' :
              billableLevel === 'warning' ? 'border-l-4 border-l-accent-warning-500 bg-gradient-to-br from-accent-warning-50/50 to-transparent dark:from-accent-warning-900/20' :
              'border-l-4 border-l-accent-destructive-500 bg-gradient-to-br from-accent-destructive-50/50 to-transparent dark:from-accent-destructive-900/20'
            } rounded-lg border bg-card shadow-sm`}
            aria-label="View billable revenue report"
          >
            <div className="flex flex-row items-center justify-between space-y-0 pb-1.5 pt-3 px-3">
              <h3 className="text-sm font-semibold tracking-tight">Billable Revenue</h3>
              <div className="relative">
                <DollarSign className={`h-5 w-5 group-hover/card:scale-110 transition-transform ${
                  billableLevel === 'excellent' ? 'text-accent-success-500' :
                  billableLevel === 'good' ? 'text-accent-info-500' :
                  billableLevel === 'warning' ? 'text-accent-warning-500' :
                  'text-accent-destructive-500'
                }`} />
                <ExternalLink className="h-3 w-3 absolute -top-1 -right-1 opacity-0 group-hover/card:opacity-100 transition-opacity text-muted-foreground" />
              </div>
            </div>
            <div className="px-3 pb-3 pt-1">
              <div className={`text-3xl font-bold ${
                billableLevel === 'excellent' ? 'text-accent-success-600 dark:text-accent-success-400' :
                billableLevel === 'good' ? 'text-accent-info-600 dark:text-accent-info-400' :
                billableLevel === 'warning' ? 'text-accent-warning-600 dark:text-accent-warning-400' :
                'text-accent-destructive-600 dark:text-accent-destructive-400'
              }`}>
                {stats.billablePercentage}%
              </div>
              <p className={`text-xs font-medium mt-1 ${
                billableLevel === 'excellent' ? 'text-accent-success-600 dark:text-accent-success-400' :
                billableLevel === 'good' ? 'text-accent-info-600 dark:text-accent-info-400' :
                billableLevel === 'warning' ? 'text-accent-warning-600 dark:text-accent-warning-400' :
                'text-accent-destructive-600 dark:text-accent-destructive-400'
              }`}>
                {billableLevel === 'excellent' ? '🎯 Excellent!' :
                 billableLevel === 'good' ? '✓ Above target' :
                 billableLevel === 'warning' ? '⚠ Below target (75%)' :
                 '⚠ Critical - Focus needed'}
              </p>
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <p className="font-semibold mb-1">Billable Revenue Percentage</p>
          <p className="text-xs text-muted-foreground">
            {stats.billableHours.toFixed(1)}h of {stats.currentWeekHours.toFixed(1)}h this week are billable to clients.
            {billableLevel === 'excellent' ? ' Outstanding performance - keep it up!' :
             billableLevel === 'good' ? ' Good work - above the 75% target.' :
             billableLevel === 'warning' ? ' Below target - try to increase billable work.' :
             ' Critical - focus on billable projects.'}
          </p>
          <p className="text-xs text-muted-foreground mt-2 font-medium">
            💡 Click to view detailed billable hours report
          </p>
        </TooltipContent>
      </Tooltip>

      {/* This Month Card - Clickable with visual accents and tooltip */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => onNavigate?.('/reports/timesheet')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigate?.('/reports/timesheet');
              }
            }}
            className={`w-full text-left group/card hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer ${
              stats.submittedThisMonth >= 4 ? 'border-l-4 border-l-accent-success-500 bg-gradient-to-br from-accent-success-50/30 to-transparent dark:from-accent-success-900/10' :
              stats.submittedThisMonth >= 3 ? 'border-l-4 border-l-accent-info-500 bg-gradient-to-br from-accent-info-50/30 to-transparent dark:from-accent-info-900/10' :
              'border-l-4 border-l-accent-warning-500 bg-gradient-to-br from-accent-warning-50/30 to-transparent dark:from-accent-warning-900/10'
            } rounded-lg border bg-card shadow-sm`}
            aria-label="View monthly timesheet report"
          >
            <div className="flex flex-row items-center justify-between space-y-0 pb-1.5 pt-3 px-3">
              <h3 className="text-sm font-semibold tracking-tight">This Month</h3>
              <div className="relative">
                <Calendar className={`h-5 w-5 group-hover/card:scale-110 transition-transform ${
                  stats.submittedThisMonth >= 4 ? 'text-accent-success-500' :
                  stats.submittedThisMonth >= 3 ? 'text-accent-info-500' :
                  'text-accent-warning-500'
                }`} />
                <ExternalLink className="h-3 w-3 absolute -top-1 -right-1 opacity-0 group-hover/card:opacity-100 transition-opacity text-muted-foreground" />
              </div>
            </div>
            <div className="px-3 pb-3 pt-1">
              <div className={`text-3xl font-bold ${
                stats.submittedThisMonth >= 4 ? 'text-accent-success-600 dark:text-accent-success-400' :
                stats.submittedThisMonth >= 3 ? 'text-accent-info-600 dark:text-accent-info-400' :
                'text-accent-warning-600 dark:text-accent-warning-400'
              }`}>
                {stats.submittedThisMonth}
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                Timesheets submitted
              </p>
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <p className="font-semibold mb-1">Monthly Submissions</p>
          <p className="text-xs text-muted-foreground">
            You've submitted {stats.submittedThisMonth} {stats.submittedThisMonth === 1 ? 'timesheet' : 'timesheets'} this month.
            {stats.submittedThisMonth >= 4 ? ' Great consistency!' :
             stats.submittedThisMonth >= 3 ? ' Good progress.' :
             ' Keep submitting regularly.'}
          </p>
          <p className="text-xs text-muted-foreground mt-2 font-medium">
            💡 Click to view monthly timesheet report
          </p>
        </TooltipContent>
      </Tooltip>

      {/* Avg Weekly Card - Clickable with visual accents and tooltip */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => onNavigate?.('/reports/utilization')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigate?.('/reports/utilization');
              }
            }}
            className={`w-full text-left group/card hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer ${
              avgWeeklyLevel === 'excellent' ? 'border-l-4 border-l-accent-success-500 bg-gradient-to-br from-accent-success-50/30 to-transparent dark:from-accent-success-900/10' :
              avgWeeklyLevel === 'good' ? 'border-l-4 border-l-accent-info-500 bg-gradient-to-br from-accent-info-50/30 to-transparent dark:from-accent-info-900/10' :
              avgWeeklyLevel === 'warning' ? 'border-l-4 border-l-accent-warning-500 bg-gradient-to-br from-accent-warning-50/30 to-transparent dark:from-accent-warning-900/10' :
              'border-l-4 border-l-accent-destructive-500 bg-gradient-to-br from-accent-destructive-50/30 to-transparent dark:from-accent-destructive-900/10'
            } rounded-lg border bg-card shadow-sm`}
            aria-label="View utilization report"
          >
            <div className="flex flex-row items-center justify-between space-y-0 pb-1.5 pt-3 px-3">
              <h3 className="text-sm font-semibold tracking-tight">Avg Weekly</h3>
              <div className="relative">
                <TrendingUp className={`h-5 w-5 group-hover/card:scale-110 transition-transform ${
                  avgWeeklyLevel === 'excellent' ? 'text-accent-success-500' :
                  avgWeeklyLevel === 'good' ? 'text-accent-info-500' :
                  avgWeeklyLevel === 'warning' ? 'text-accent-warning-500' :
                  'text-accent-destructive-500'
                }`} />
                <ExternalLink className="h-3 w-3 absolute -top-1 -right-1 opacity-0 group-hover/card:opacity-100 transition-opacity text-muted-foreground" />
              </div>
            </div>
            <div className="px-3 pb-3 pt-1">
              <div className={`text-3xl font-bold ${
                avgWeeklyLevel === 'excellent' ? 'text-accent-success-600 dark:text-accent-success-400' :
                avgWeeklyLevel === 'good' ? 'text-accent-info-600 dark:text-accent-info-400' :
                avgWeeklyLevel === 'warning' ? 'text-accent-warning-600 dark:text-accent-warning-400' :
                'text-accent-destructive-600 dark:text-accent-destructive-400'
              }`}>
                {stats.avgWeeklyHours.toFixed(1)}h
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                Last 4 weeks average
              </p>
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <p className="font-semibold mb-1">Average Weekly Hours</p>
          <p className="text-xs text-muted-foreground">
            Your average is {stats.avgWeeklyHours.toFixed(1)} hours per week over the last 4 weeks.
            {avgWeeklyLevel === 'excellent' ? ' Excellent consistency!' :
             avgWeeklyLevel === 'good' ? ' Good work pattern.' :
             avgWeeklyLevel === 'warning' ? ' Consider logging more hours.' :
             ' Significantly below target.'}
          </p>
          <p className="text-xs text-muted-foreground mt-2 font-medium">
            💡 Click to view detailed utilization report
          </p>
        </TooltipContent>
      </Tooltip>
    </>
  );
}
